import React, { useContext, useEffect, useState } from 'react';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';

import AssetInventoryV2Context from '@totem/components/services/assetInventory/v2/AssetInventoryV2Context';
import {
  CHECKS,
  getCheckCount,
  isCheckApplicable,
} from '@totem/components/services/assetInventory/v2/checks';
import DetailsTab from '@totem/components/services/assetInventory/v2/DetailsTab';
import FlaggedItemsTab from '@totem/components/services/assetInventory/v2/FlaggedItemsTab';
import SummaryTab from '@totem/components/services/assetInventory/v2/SummaryTab';
import {
  AssetInventorySummary,
  TabKey,
} from '@totem/components/services/assetInventory/v2/types';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import DropDownFilters from '@totem/components/ui_components/reportSettings/dropDownFilters';

import './assetInventoryV2.css';

const FILTER_KEYS = ['systemType', 'building', 'region', 'type', 'vendor'];

// Critical and High checks are the ones the customer is asked to act on this quarter.
const needsActionCount = (summary: AssetInventorySummary) =>
  CHECKS.filter(
    (check) =>
      (check.severity === 'Critical' || check.severity === 'High') &&
      isCheckApplicable(check, summary),
  ).reduce((total, check) => total + getCheckCount(check, summary), 0);

export const AssetInventoryV2Scope = () => {
  const { summary } = useContext(AssetInventoryV2Context);

  if (!summary) {
    return null;
  }

  const { assets, buildings } = summary.totals;
  return (
    <span styleName="scope">
      {assets} asset{assets === 1 ? '' : 's'} identified by IB monitoring across{' '}
      {buildings} building{buildings === 1 ? '' : 's'}, whether or not IB
      manages them · IB infrastructure is excluded
    </span>
  );
};

const AssetInventoryV2Layout = () => {
  const {
    summary,
    summaryLoading,
    totalDevices,
    activeTab,
    setActiveTab,
    exportCsv,
    exporting,
  } = useContext(AssetInventoryV2Context);
  const [printing, setPrinting] = useState<boolean>(false);

  // Printing lays out all three tabs in sequence, then returns to the tab view.
  useEffect(() => {
    if (!printing) {
      return undefined;
    }
    const done = () => setPrinting(false);
    window.addEventListener('afterprint', done);
    const timer = setTimeout(() => window.print(), 300);
    return () => {
      clearTimeout(timer);

      window.removeEventListener('afterprint', done);
    };
  }, [printing]);

  const needsAction = summary ? needsActionCount(summary) : 0;

  const actions = (
    <div styleName="toolbar-actions no-print">
      <Button
        icon={<DownloadOutlined />}
        loading={exporting}
        disabled={totalDevices === 0}
        onClick={exportCsv}
      >
        Export CSV ({totalDevices})
      </Button>
      <Button icon={<PrinterOutlined />} onClick={() => setPrinting(true)}>
        Print / PDF
      </Button>
    </div>
  );

  const body =
    !summary && summaryLoading ? (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    ) : printing ? (
      <>
        <h3>Summary</h3>
        <SummaryTab />
        <h3 styleName="print-section">Flagged Items</h3>
        <FlaggedItemsTab expandAll />
        <h3 styleName="print-section">Details</h3>
        <DetailsTab />
      </>
    ) : (
      <Spin spinning={summaryLoading}>
        <IBTabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TabKey)}
          tabBarExtraContent={actions}
          items={[
            { key: 'summary', label: 'Summary', children: <SummaryTab /> },
            {
              key: 'flagged',
              label: (
                <span>
                  Flagged Items
                  {needsAction > 0 && (
                    <span styleName="tab-badge">{needsAction} need action</span>
                  )}
                </span>
              ),
              children: <FlaggedItemsTab />,
            },
            { key: 'details', label: 'Details', children: <DetailsTab /> },
          ]}
        />
      </Spin>
    );

  return (
    <div styleName="report">
      <div styleName="no-print">
        <DropDownFilters showFilterChips keys={FILTER_KEYS} extra={''} />
      </div>
      {body}
    </div>
  );
};

export default AssetInventoryV2Layout;
